import React, { useState, useTransition } from "react";
import { useNavigate, Link } from "react-router-dom";

import { Box, Card, CardContent, Typography, Stack, Grid } from "@mui/material";
import API from "../config/axios";
import { toast } from "react-hot-toast";
import CustomBtn from "../components/CustomBtn";
import { useTheme } from "@mui/material/styles";
import TextInput from "../components/form/TextInput";

import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

const defaultValues = {
  email: "",
};

export default function ForgotPassword() {
  const isDarkTheme = useTheme().palette.mode === "dark";
  const logoLink = isDarkTheme ? "/logo_light.svg" : "/logo.svg";
  const { t } = useTransition();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [inputErrors, setInputErrors] = useState({});

  const validationSchema = yup.object().shape({
    email: yup.string().required("email is required").email("email is invalid"),
  });

  const { handleSubmit, control } = useForm({
    defaultValues: defaultValues,
    mode: "onBlur",
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = (data) => {
    setIsLoading(true);

    const requestStartTime = Date.now();

    API.post("auth/admin/forgot-password", {
      email: data.email,
    })
      .then((res) => {
        const resData = res.data;

        setInputErrors({});

        if (resData.status) {
          toast.success(resData.message);

          navigate("/login", { replace: true });
        } else {
          if (resData && resData.type === "VALIDATION") {
            setInputErrors(resData.error);
          } else {
            toast.error("Something went wrong");
          }
        }
      })
      .catch(() => {
        toast.error("Something went wrong");
      })
      .finally(() => {
        const requestEndTime = Date.now();
        const timeOutMileSeconds = requestEndTime - requestStartTime;

        if (timeOutMileSeconds > 300) {
          setIsLoading(false);
        } else {
          setTimeout(() => {
            setIsLoading(false);
          }, 300);
        }
      });
  };

  return (
    <>
      <img
        src={logoLink}
        width={"150px"}
        alt="logo"
        style={{ position: "relative", top: "30px", left: "30px" }}
      />
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Stack sx={{ marginX: 2, marginY: 10 }}>
          <Card
            elevation={1}
            sx={{
              p: 2,
              maxWidth: "450px",
              width: "min(90vw, 450px)",
              boxShadow: isDarkTheme
                ? "rgba(0, 0, 0, 0.1) 0px 10px 50px"
                : "rgba(0, 0, 0, 0.1) 0px 10px 50px",
            }}
          >
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3} columns={12}>
                  <Grid item xs={12}>
                    <Typography variant="h5" fontWeight={"medium"} gutterBottom>
                      Forgot Password
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput
                      name="email"
                      type="email"
                      label="Email Address"
                      autoFocus={true}
                      control={control}
                      placeholder="demo@company.com"
                      customError={inputErrors.email || null}
                    />
                  </Grid>
                  <Grid item xs={12} mt={1}>
                    <CustomBtn
                      variant="contained"
                      color="secondary"
                      fullWidth
                      size="large"
                      disableRipple
                      type="submit"
                      loading={isLoading}
                    >
                      {t("common_btn.submit")}
                    </CustomBtn>
                  </Grid>
                  <Grid item xs={12} mt={1}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Link to="/login" className="custom-link">
                        <Typography
                          color="secondary"
                          sx={{ cursor: "pointer" }}
                          display={"inline"}
                          variant="body2"
                        >
                          Back to Login
                        </Typography>
                      </Link>
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </>
  );
}
